// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridProperties.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * other settings model
 */
import { action, computed, observable } from 'mobx';
import { get, getOr, cloneDeep, toNumber } from 'lodash/fp';

import tracker from 'share/tracking/Izenda.Tracker';
import setValue from 'share/utils/Izenda.setValue';
import {
  DEFAULT_VALUE_PROPERTIES as defaultValue,
  GRID_STYLES,
  FREEZE_HEADERS,
  SEPARATOR_STYLE,
  DEFAULT_SETTING_VALUE
} from 'logic/data/Izenda.ReportPartGridConstants';
import UIConstants from 'logic/data/Izenda.UIConstants';
import { DEFAULT_PAGE_SIZE } from 'logic/data/Izenda.ReportDesignerConstants';

/**
 * report part grid properties
 * @constructor ReportPartGridProperties
 * @param  {json} data JSON data from api
 */
@tracker
export default class ReportPartGridProperties {
  @observable
  generalInfo = {
    gridStyle: GRID_STYLES.VERTICAL.value,
    separatorStyle: SEPARATOR_STYLE.COMMA.value
  };

  @observable
  table = {
    border: cloneDeep(DEFAULT_SETTING_VALUE.border),
    backgroundColor: UIConstants.reportPartGridDefault.backgroundColor,
    alternativeBackgroundColor: defaultValue.TABLE.ALTERNATIVE_BACKGROUND_COLOR
  };

  @observable
  columns = {
    width: {
      value: defaultValue.COLUMNS.WIDTH.VALUE
    },
    alterBackgroundColor: false,
    wordWrap: defaultValue.COLUMNS.WORDWRAP,
    alignment: defaultValue.COLUMNS.ALIGNMENT,
    verticalAlignment: defaultValue.COLUMNS.VERTICAL_ALIGNMENT
  };

  @observable
  rows = {
    alterBackgroundColor: false
  };

  @observable
  headers = {
    font: {
      family: defaultValue.HEADERS.FONT.FAMILY,
      size: defaultValue.HEADERS.FONT.SIZE,
      bold: defaultValue.HEADERS.FONT.BOLD,
      italic: defaultValue.HEADERS.FONT.ITALIC,
      underline: defaultValue.HEADERS.FONT.UNDERLINE,
      color: defaultValue.HEADERS.FONT.COLOR,
      backgroundColor: defaultValue.HEADERS.FONT.BACKGROUND_COLOR
    },
    alignment: defaultValue.HEADERS.ALIGNMENT,
    verticalAlignment: defaultValue.HEADERS.VERTICAL_ALIGNMENT,
    wordWrap: defaultValue.HEADERS.WORDWRAP,
    removeHeaderForExport: false,
    removeHeaderForViewer: false,
    freezeHeaders: null
  };

  @observable
  grouping = {
    useSeparator: true
  };

  @observable
  view = {
    dataRefreshInterval: {
      enable: false,
      updateInterval: 0,
      isAll: true,
      latestRecord: 0
    },
    conditionalFormatting: {
      rules: []
    },
    usePagination: true,
    expanderRowsSpacing: false,
    collapseDrilldownByDefault: false,
    pageSize: DEFAULT_PAGE_SIZE
  };

  @observable
  printing = {
    usePageBreakAfterSeparator: false
  };

  @computed
  get pageSize() {
    return this.view.pageSize;
  }

  @computed
  get isHeaderStickVertical() {
    return this.headers.freezeHeaders === FREEZE_HEADERS.VERTICALLY.value;
  }

  @computed
  get isHeaderStickHorizontal() {
    return this.headers.freezeHeaders === FREEZE_HEADERS.HORIZONTALLY.value;
  }

  constructor(data) {
    this.view.pivotColumnsPerExportedPage = '';
    data && this.setReportPartProperties(data);

    this.cleanDirty();
  }

  /**
   * get properties from table object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getTableByKey(key) {
    return get(key, this.table);
  }

  /**
   * get properties from headers object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getHeadersByKey(key) {
    return get(key, this.headers);
  }

  /**
   * get properties from columns object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getColumnsByKey(key) {
    return get(key, this.columns);
  }

  /**
   * get properties from columns object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getColumnWidth() {
    return get('width.value', this.columns);
  }

  /**
   * get properties from rows object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getRowsByKey(key) {
    return get(key, this.rows);
  }

  /**
   * get properties from general info object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getGeneralInfoByKey(key) {
    return get(key, this.generalInfo);
  }

  /**
   * get properties from grouping object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getGroupingByKey(key) {
    return get(key, this.grouping);
  }

  /**
   * get properties from grouping object
   * @param  {string} key name of properties
   * @return {object}     value of properties
   */
  getViewByKey(key) {
    return get(key, this.view);
  }

  /**
   * update repoert part properties from data from properties editor or api
   * @param {object} data [update data]
   */
  @action
  setReportPartProperties(data) {
    setValue(this.generalInfo, 'gridStyle', get('generalInfo.gridStyle', data));
    setValue(this.generalInfo, 'separatorStyle', get('generalInfo.separatorStyle', data));
    this.setTable(data.table);
    setValue(this.rows, 'alterBackgroundColor', get('rows.alterBackgroundColor', data));
    setValue(this.grouping, 'useSeparator', get('grouping.useSeparator', data));
    setValue(this.printing, 'usePageBreakAfterSeparator', get('printing.usePageBreakAfterSeparator', data));
    this.setHeaders(data.headers);
    this.setView(data.view);
    this.setColumns(data.columns);

    this.markDirty();
  }

  @action
  setTable(data) {
    const setDetail = action(function setDetail(key) {
      let detail = get(key, border);
      setValue(table.border[key], 'color', getOr(null, 'color', detail));
      setValue(table.border[key], 'dashStyle', getOr(null, 'dashStyle', detail));
      setValue(table.border[key], 'thinkness', getOr(null, 'thinkness', detail));
    });

    let table = this.table;
    let border = get('border', data);

    setValue(table, 'backgroundColor', get('backgroundColor', data));
    setValue(table, 'alternativeBackgroundColor', get('alternativeBackgroundColor', data));
    setDetail('bottom');
    setDetail('left');
    setDetail('midHor');
    setDetail('midVer');
    setDetail('right');
    setDetail('top');
  }

  @action
  setColumns(data) {
    const columns = this.columns;

    setValue(columns.width, 'value', get('width.value', data));
    setValue(columns, 'alterBackgroundColor', get('alterBackgroundColor', data));
    setValue(columns, 'wordWrap', get('wordWrap', data));
    setValue(columns, 'alignment', get('alignment', data));
    setValue(columns, 'verticalAlignment', get('verticalAlignment', data));
  }

  @action
  setView(data) {
    const view = this.view;

    setValue(view, 'dataRefreshInterval', get('dataRefreshInterval', data));
    setValue(view, 'conditionalFormatting', get('conditionalFormatting', data));
    setValue(view, 'usePagination', get('usePagination', data));
    setValue(view, 'expanderRowsSpacing', get('expanderRowsSpacing', data));
    setValue(view, 'collapseDrilldownByDefault', get('collapseDrilldownByDefault', data));
    setValue(view, 'pivotColumnsPerExportedPage', toNumber(get('pivotColumnsPerExportedPage', data)) || '');
    setValue(view, 'pageSize', get('pageSize', data));
  }

  /**
   * set Headers
   */
  @action
  setHeaders(data) {
    const headers = this.headers;

    setValue(headers.font, 'family', get('font.family', data));
    setValue(headers.font, 'size', get('font.size', data));
    setValue(headers.font, 'bold', get('font.bold', data));
    setValue(headers.font, 'italic', get('font.italic', data));
    setValue(headers.font, 'underline', get('font.underline', data));
    setValue(headers.font, 'color', get('font.color', data));
    setValue(headers.font, 'backgroundColor', get('font.backgroundColor', data));

    setValue(headers, 'alignment', get('alignment', data));
    setValue(headers, 'verticalAlignment', get('verticalAlignment', data));
    setValue(headers, 'wordWrap', get('wordWrap', data));
    setValue(headers, 'removeHeaderForExport', get('removeHeaderForExport', data));
    setValue(headers, 'removeHeaderForViewer', get('removeHeaderForViewer', data));
    setValue(headers, 'freezeHeaders', get('freezeHeaders', data));
  }

  /**
   * Get Header Cell Style
   * @return {Object}
   */
  getHeaderCellStyle() {
    const headers = this.headers;
    return {
      fontFamily: get('font.family', headers),
      fontSize: get('font.size', headers),
      bold: get('font.bold', headers),
      italic: get('font.italic', headers),
      underline: get('font.underline', headers),
      textColor: get('font.color', headers),
      backgroundColor: get('font.backgroundColor', headers),
      alignment: get('alignment', headers),
      verticalAlignment: get('verticalAlignment', headers),
      wordWrap: get('wordWrap', headers)
    };
  }

  /**
   * Get dirty status.
   */
  checkDirty() {
    return this.isDirty;
  }

  @action
  changePageSize(pageSize) {
    this.view.pageSize = pageSize;
    this.markDirty();
  }

  /**
   * Clean dirty status.
   */
  cleanDirty() {
    this.resetDirty();
  }

  getPageBreakAfterSeparator(key) {
    return get(key, this.printing);
  }

  /**
   * Reset border default value
   */
  @action
  resetBorderDefaultValue() {
    this.table.border = cloneDeep(DEFAULT_SETTING_VALUE.border);
    this.markDirty();
  }

  /**
   * Reset refresh interval setting to default value
   */
  @action
  resetRefreshInterval() {
    this.view.dataRefreshInterval = DEFAULT_SETTING_VALUE.refreshIntervalDefault;
    this.markDirty();
  }

  /**
   * Reset conditional formatting setting to default value
   */
  @action
  resetConditionalFormatting() {
    this.view.conditionalFormatting = DEFAULT_SETTING_VALUE.conditionalFormattingDefault;
    this.markDirty();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartGridProperties.js